import { Dialog } from "@headlessui/react";
import { AiOutlineClose } from "react-icons/ai";
import PillarGraphic from "./PillarGraphic";
import ProjectPublicationList from "./ProjectPublicationList";
import Carousel from "./Carousel";
import { getProjectContent } from "../utils/projectContent";
import { researchPillars } from "../data/researchPillars";
import { formatHtml } from "../utils/formatHtml";

const pillarById = Object.fromEntries(
  researchPillars.map((pillar) => [pillar.id, pillar]),
);

const ThemeBadges = ({ pillarIds = [], onOpenPillar }) => {
  const pillars = pillarIds.map((id) => pillarById[id]).filter(Boolean);
  if (!pillars.length) return null;

  return (
    <div className="flex flex-wrap items-start justify-start gap-2 sm:justify-end">
      {pillars.map((pillar) => (
        <button
          key={pillar.id}
          type="button"
          onClick={() => onOpenPillar?.(pillar.id)}
          title={pillar.title}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/70 py-0.5 pl-0.5 pr-2.5 text-sm font-medium text-green dark:text-green-dark transition-colors hover:border-green/60 dark:border-gray-700 dark:bg-darkBg/70"
        >
          <PillarGraphic
            pillarId={pillar.id}
            className="h-6 w-8 overflow-hidden rounded-full"
          />
          <span>{pillar.bigGoal || pillar.id}</span>
        </button>
      ))}
    </div>
  );
};

const LinkedItemsSection = ({ title, items = [] }) => {
  if (!items.length) return null;

  return (
    <section className="mt-5 sm:mt-6">
      <h3 className="text-sm font-semibold text-lightText dark:text-darkText">{title}</h3>
      <ul className="mt-2 space-y-3.5 sm:space-y-3">
        {items.map((item, idx) => (
          <li
            key={`${item.title || item.date}-${idx}`}
            className="text-sm leading-relaxed text-gray-700 dark:text-gray-300"
          >
            {item.date ? (
              <p className="mb-0.5 text-sm font-semibold text-gray-500 dark:text-gray-400 sm:mb-0 sm:mr-2 sm:inline">
                {item.date}
              </p>
            ) : null}
            {/<a\s/i.test(item.title || "") ? (
              <span
                className="[&_a]:text-green dark:[&_a]:text-green-dark [&_a]:underline [&_a]:underline-offset-2"
                dangerouslySetInnerHTML={{
                  __html: formatHtml(item.title),
                }}
              />
            ) : (
              <span>{item.title}</span>
            )}
            {item.links?.slides ? (
              <>
                {" "}
                <a
                  href={item.links.slides}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green dark:text-green-dark underline underline-offset-2"
                >
                  Slides
                </a>
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

const renderDescriptionBlocks = (description) =>
  String(description || "")
    .split("\n\n")
    .map((block, i, blocks) => (
      <div key={i}>
        {block.split("\n").map((line, j) =>
          /<[^>]+>/.test(line) ? (
            <div
              key={j}
              className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300 [&_a]:text-green dark:[&_a]:text-green-dark [&_a]:underline [&_a]:underline-offset-2"
              dangerouslySetInnerHTML={{ __html: formatHtml(line) }}
            />
          ) : (
            <p
              key={j}
              className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300"
            >
              {line}
            </p>
          ),
        )}
        {i < blocks.length - 1 ? <br /> : null}
      </div>
    ));

const toYoutubeEmbedUrl = (url = "") => {
  const value = String(url || "").trim();
  if (!value) return "";
  if (/youtube\.com\/embed\//i.test(value)) return value;
  const watchMatch = value.match(/[?&]v=([^&]+)/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
  const shortMatch = value.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
  return value;
};

const ProjectModal = ({ projectId, onClose, onOpenPillar }) => {
  const content = projectId ? getProjectContent(projectId) : null;
  const isOpen = Boolean(projectId);
  const project = content?.project;
  const isPrePhd = content?.collection === "pre-phd";
  const carouselData = project?.carouselData || [];
  const youtubeEmbed = toYoutubeEmbedUrl(project?.youtube);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-hidden"
    >
      <div className="flex h-full items-end justify-center sm:items-center sm:p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-[1px]" />

        <div className="relative z-10 flex h-[92dvh] max-h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-lightBg shadow-xl dark:border-gray-700 dark:bg-darkBg sm:h-[75vh] sm:max-h-[75vh] sm:w-[min(100%,75vw)] sm:rounded-xl">
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-6">
            <div className="min-w-0 flex-1">
              {project ? (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-3">
                    <Dialog.Title className="text-base font-semibold leading-snug text-lightText dark:text-darkText sm:text-xl">
                      {project.title}
                    </Dialog.Title>
                    <ThemeBadges
                      pillarIds={project.pillarIds}
                      onOpenPillar={onOpenPillar}
                    />
                  </div>
                  {project.role && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {project.role}
                    </p>
                  )}
                  {project.duration && (
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                      {project.duration}
                    </p>
                  )}
                </>
              ) : (
                <Dialog.Title className="text-lg font-semibold text-lightText dark:text-darkText">
                  Project not found
                </Dialog.Title>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="-mr-1 shrink-0 rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-green dark:hover:text-green-dark dark:hover:bg-gray-800"
              aria-label="Close"
            >
              <AiOutlineClose size={18} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-5">
            {!content ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This project could not be found.
              </p>
            ) : (
              <>
                {!isPrePhd && project.img && (
                  <div className="mb-5 flex max-h-48 w-full items-center justify-center rounded-md border border-gray-200/70 bg-white p-4 dark:border-gray-800/70 dark:bg-darkBg">
                    <img
                      src={project.img}
                      alt={project.title}
                      className="max-h-40 max-w-full object-contain"
                    />
                  </div>
                )}

                <section>
                  <h3 className="text-sm font-semibold text-lightText dark:text-darkText">
                    {isPrePhd ? "About the Project" : "Overview"}
                  </h3>
                  {isPrePhd && project.description ? (
                    <div className="mt-2">
                      {renderDescriptionBlocks(project.description)}
                    </div>
                  ) : project.summary ? (
                    <div className="mt-2 rounded-lg border border-green/25 border-l-4 border-l-green bg-green/5 px-4 py-3 dark:bg-green/10">
                      <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                        {project.summary}
                      </p>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Overview coming soon.
                    </p>
                  )}
                </section>

                {isPrePhd && carouselData.length > 0 && (
                  <section className="mt-5 sm:mt-6">
                    <h3 className="text-sm font-semibold text-lightText dark:text-darkText">Gallery</h3>
                    <div className="mt-3">
                      <Carousel carouselData={carouselData} />
                    </div>
                  </section>
                )}

                {isPrePhd && youtubeEmbed && (
                  <section className="mt-5 sm:mt-6">
                    <h3 className="text-sm font-semibold text-lightText dark:text-darkText">
                      Demo Video
                    </h3>
                    <div className="aspect-w-16 aspect-h-9 mt-3 overflow-hidden rounded-md border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800">
                      <iframe
                        src={youtubeEmbed}
                        title={`${project.title} demo video`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="h-full w-full"
                      />
                    </div>
                  </section>
                )}

                {isPrePhd && (project.github || project.report) && (
                  <div className="mt-5 flex flex-wrap gap-3 sm:mt-6">
                    {project.github ? (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded bg-darkBg px-4 py-2 text-sm text-darkText transition-colors hover:bg-green-solid hover:text-white dark:bg-lightBg dark:text-lightText dark:hover:bg-green-solid dark:hover:text-white"
                      >
                        View GitHub Repository
                      </a>
                    ) : null}
                    {project.report ? (
                      <a
                        href={project.report}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded bg-darkBg px-4 py-2 text-sm text-darkText transition-colors hover:bg-green-solid hover:text-white dark:bg-lightBg dark:text-lightText dark:hover:bg-green-solid dark:hover:text-white"
                      >
                        Read More
                      </a>
                    ) : null}
                  </div>
                )}

                {!isPrePhd && (
                  <>
                    <ProjectPublicationList
                      title="Publications / Papers"
                      items={content.relatedPublications}
                    />
                    <ProjectPublicationList
                      title="Workshops"
                      items={content.workshops}
                      showWorkshopTitle
                    />
                    <LinkedItemsSection
                      title="Talks and Presentations"
                      items={content.talks}
                    />
                    <LinkedItemsSection
                      title="Media Coverage"
                      items={content.mediaCoverage}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ProjectModal;
